import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Chip } from '@/components/Chip/Chip';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import {
  DocumentForm,
  type DocumentDraft,
  type DocumentFormMode,
} from '@/components/DocumentForm/DocumentForm';
import { DocumentHistory } from '@/components/DocumentHistory/DocumentHistory';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Icon } from '@/components/Icon/Icon';
import { LenderDocumentList } from '@/components/LenderDocumentList/LenderDocumentList';
import { LenderForm, type LenderDraft } from '@/components/LenderForm/LenderForm';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { Spinner } from '@/components/Spinner/Spinner';
import { ME_QUERY, type MeQueryResult } from '@/graphql/keystone';
import {
  CREATE_LENDER_DOCUMENT,
  DELETE_LENDER,
  DELETE_LENDER_DOCUMENT,
  FILE_DOWNLOAD_QUERY,
  LENDER_DETAIL_QUERY,
  PUBLISH_LENDER_DOCUMENT_VERSION,
  REORDER_LENDER_DOCUMENTS,
  REVERT_LENDER_DOCUMENT,
  SET_LENDER_DOCUMENT_STATUS,
  SET_LENDER_STATUS,
  UPDATE_LENDER,
  type FileDownloadResult,
  type LenderDetailResult,
  type LenderDocument,
  type LenderDocumentVersion,
} from '@/graphql/lenders';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { countryFlag, countryName, extensionOf, moveId, sortDocuments } from '@/lib/lenders';
import { uploadFile, type UploadProgress } from '@/lib/upload';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './LendersContainer.module.css';

type Confirm =
  | { kind: 'deactivate' }
  | { kind: 'delete-lender' }
  | { kind: 'archive'; document: LenderDocument }
  | { kind: 'delete-document'; document: LenderDocument }
  | { kind: 'revert'; document: LenderDocument; version: LenderDocumentVersion };

type DocumentFormState = { mode: DocumentFormMode; document: LenderDocument | null };

/** Saves a signed URL to disk without leaving the page. */
function saveFrom(url: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function titleOf(document: LenderDocument): string {
  return document.currentVersion?.title ?? document.key ?? 'this document';
}

/**
 * `/lenders/:key`: one lender, its documents in the order clients are given
 * them, and each document's history. Every write refetches the lender, since
 * a version, a reorder and an archive each change what several rows say.
 */
export function LenderContainer() {
  const { key = '' } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { access, refetch: recheckAccess } = useResourceAccess('lenders');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';

  const me = useQuery<MeQueryResult>(ME_QUERY, { skip: !canRead });
  const detail = useQuery<LenderDetailResult, { key: string }>(LENDER_DETAIL_QUERY, {
    variables: { key },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  const refetchOptions = {
    refetchQueries: ['LenderDetail', 'Lenders'],
    awaitRefetchQueries: true,
  };
  const [updateLender] = useMutation(UPDATE_LENDER, refetchOptions);
  const [setLenderStatus] = useMutation(SET_LENDER_STATUS, refetchOptions);
  const [deleteLender] = useMutation(DELETE_LENDER);
  const [createDocument] = useMutation(CREATE_LENDER_DOCUMENT, refetchOptions);
  const [publishVersion] = useMutation(PUBLISH_LENDER_DOCUMENT_VERSION, refetchOptions);
  const [setDocumentStatus] = useMutation(SET_LENDER_DOCUMENT_STATUS, refetchOptions);
  const [reorderDocuments] = useMutation(REORDER_LENDER_DOCUMENTS, refetchOptions);
  const [deleteDocument] = useMutation(DELETE_LENDER_DOCUMENT, refetchOptions);
  const [revertDocument] = useMutation(REVERT_LENDER_DOCUMENT, refetchOptions);

  const [editingLender, setEditingLender] = useState(false);
  const [documentForm, setDocumentForm] = useState<DocumentFormState | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  // The file this dialog already uploaded, so a retry after a refusal reuses it.
  const uploaded = useRef<{ file: File; id: string } | null>(null);

  const lender = detail.data?.lenders[0] ?? null;
  const documents = useMemo(() => sortDocuments(lender?.documents ?? []), [lender]);
  const openDocument = documents.find((document) => document.id === openDocumentId) ?? null;

  if (!canRead) {
    return <AccessNotice area="lenders" onRetry={recheckAccess} state={access as 'checking'} />;
  }

  const back = <Link to="/lenders">← All lenders</Link>;

  if (!lender) {
    if (detail.error) {
      return (
        <>
          <PageHeader back={back} title="Lender" />
          <Callout tone="danger" title="This lender couldn't be loaded">
            {friendlyError(detail.error)}
          </Callout>
        </>
      );
    }
    if (detail.loading || !detail.data) {
      return (
        <p className={styles.loading} role="status">
          <Spinner /> Loading lender…
        </p>
      );
    }
    return (
      <>
        <PageHeader back={back} title="Lender not found" />
        <EmptyState
          action={
            <Link className={styles.backLink} to="/lenders">
              See every lender
            </Link>
          }
          icon="buildings"
          title="There's no lender here"
        >
          <p>It may have been deleted. Go back to see every lender.</p>
        </EmptyState>
      </>
    );
  }

  const lenderName = lender.name ?? lender.key ?? 'Lender';
  const inactive = lender.status === 'INACTIVE';

  /** Runs one write; a refusal lands in `into`, a permission one rechecks access. */
  const run = async (
    call: () => Promise<unknown>,
    into: (message: string | null) => void,
    success?: string,
  ): Promise<boolean> => {
    setBusy(true);
    into(null);
    try {
      await call();
      if (success) {
        toast.success({ title: success });
      }
      return true;
    } catch (cause) {
      into(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSaveLender = async (draft: LenderDraft) => {
    const ok = await run(
      () =>
        updateLender({ variables: { id: lender.id, name: draft.name, country: draft.country } }),
      setFormError,
      'Lender updated',
    );
    if (ok) {
      setEditingLender(false);
    }
  };

  const onActivate = async () => {
    setActivating(true);
    await run(
      () => setLenderStatus({ variables: { id: lender.id, status: 'ACTIVE' } }),
      setPageError,
      `${lenderName} is active again`,
    );
    setActivating(false);
  };

  const onSubmitDocument = async (draft: DocumentDraft) => {
    if (!documentForm) {
      return;
    }
    const { mode, document } = documentForm;
    const ok = await run(
      async () => {
        let fileId: string | undefined;
        if (draft.file && uploaded.current?.file === draft.file) {
          fileId = uploaded.current.id;
        } else if (draft.file) {
          const file = await uploadFile(client, draft.file, { onProgress: setProgress });
          uploaded.current = { file: draft.file, id: file.id };
          fileId = file.id;
        }
        if (mode === 'add') {
          await createDocument({
            variables: { lenderId: lender.id, key: draft.key, fileId, title: draft.title },
          });
        } else if (document) {
          const titleChanged = draft.title !== (document.currentVersion?.title ?? '');
          await publishVersion({
            variables: {
              documentId: document.id,
              fileId,
              title: titleChanged ? draft.title : undefined,
            },
          });
        }
      },
      setFormError,
      mode === 'add'
        ? `${draft.title} added`
        : mode === 'replace'
          ? 'New version published'
          : 'Document renamed',
    );
    setProgress(null);
    if (ok) {
      setDocumentForm(null);
    }
  };

  const onMove = async (document: LenderDocument, step: -1 | 1) => {
    const orderedIds = moveId(
      documents.map((row) => row.id),
      document.id,
      step,
    );
    if (!orderedIds) {
      return;
    }
    setReordering(true);
    await run(
      () => reorderDocuments({ variables: { lenderId: lender.id, orderedIds } }),
      setPageError,
    );
    setReordering(false);
  };

  const onRestore = (document: LenderDocument) =>
    run(
      () => setDocumentStatus({ variables: { id: document.id, status: 'ACTIVE' } }),
      setPageError,
      `${titleOf(document)} restored`,
    );

  const download = async (
    id: string,
    fileId: string | null,
    title: string,
    filename: string | null,
  ) => {
    if (!fileId) {
      return;
    }
    setDownloading(id);
    setPageError(null);
    try {
      const { data } = await client.query<FileDownloadResult>({
        query: FILE_DOWNLOAD_QUERY,
        variables: { id: fileId, filename: `${title}${extensionOf(filename)}` },
        fetchPolicy: 'network-only',
      });
      const url = data?.file?.originalDownloadUrl;
      if (!url) {
        throw new Error("This file isn't available to download.");
      }
      saveFrom(url);
    } catch (cause) {
      const message = friendlyError(cause);
      setPageError(message);
      toast.error({ title: 'Download failed', description: message });
    } finally {
      setDownloading(null);
    }
  };

  const onConfirm = async () => {
    if (!confirm) {
      return;
    }
    switch (confirm.kind) {
      case 'deactivate':
        if (
          await run(
            () => setLenderStatus({ variables: { id: lender.id, status: 'INACTIVE' } }),
            setFormError,
            `${lenderName} deactivated`,
          )
        ) {
          setConfirm(null);
        }
        return;
      case 'delete-lender':
        if (
          await run(
            () => deleteLender({ variables: { id: lender.id }, refetchQueries: ['Lenders'] }),
            setFormError,
            `${lenderName} deleted`,
          )
        ) {
          setConfirm(null);
          navigate('/lenders');
        }
        return;
      case 'archive':
        if (
          await run(
            () => setDocumentStatus({ variables: { id: confirm.document.id, status: 'ARCHIVED' } }),
            setFormError,
            `${titleOf(confirm.document)} archived`,
          )
        ) {
          setConfirm(null);
        }
        return;
      case 'delete-document':
        if (
          await run(
            () => deleteDocument({ variables: { id: confirm.document.id } }),
            setFormError,
            `${titleOf(confirm.document)} deleted`,
          )
        ) {
          setConfirm(null);
          setOpenDocumentId(null);
        }
        return;
      case 'revert':
        if (
          await run(
            () =>
              revertDocument({
                variables: {
                  documentId: confirm.document.id,
                  versionNo: confirm.version.versionNo,
                },
              }),
            setFormError,
            `Version ${confirm.version.versionNo} restored`,
          )
        ) {
          setConfirm(null);
        }
        return;
    }
  };

  const ask = (next: Confirm) => {
    setFormError(null);
    setConfirm(next);
  };

  const openForm = (mode: DocumentFormMode, document: LenderDocument | null) => {
    setFormError(null);
    setProgress(null);
    uploaded.current = null;
    setDocumentForm({ mode, document });
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          canEdit ? (
            <>
              <Button
                icon={<Icon name="pencil-simple" />}
                onClick={() => {
                  setFormError(null);
                  setEditingLender(true);
                }}
                variant="ghost"
              >
                Edit details
              </Button>
              {inactive ? (
                <Button
                  icon={<Icon name="eye" />}
                  loading={activating}
                  onClick={onActivate}
                  variant="ghost"
                >
                  Reactivate
                </Button>
              ) : (
                <Button
                  icon={<Icon name="eye-slash" />}
                  onClick={() => ask({ kind: 'deactivate' })}
                  variant="ghost"
                >
                  Deactivate
                </Button>
              )}
              <Button
                icon={<Icon name="trash" />}
                onClick={() => ask({ kind: 'delete-lender' })}
                variant="quiet"
              >
                Delete
              </Button>
            </>
          ) : null
        }
        back={back}
        badges={<Chip tone={inactive ? 'warn' : 'good'}>{inactive ? 'Inactive' : 'Active'}</Chip>}
        lede={
          <>
            <span aria-hidden="true">{countryFlag(lender.country)} </span>
            {countryName(lender.country)} · Reference{' '}
            <code className={styles.reference}>{lender.key}</code>
          </>
        }
        title={lenderName}
      />

      {inactive ? (
        <div className={styles.notice}>
          <Callout title="This lender is inactive">
            Clients aren’t shown {lenderName} or any of its documents.
            {canEdit ? ' Reactivate it to show them again.' : ''}
          </Callout>
        </div>
      ) : null}

      {access === 'read' ? (
        <div className={styles.notice}>
          <Callout title="You can view this lender, but not change it">
            Ask an admin for full access to lenders if you need to add or update documents.
          </Callout>
        </div>
      ) : null}

      {pageError ? (
        <div className={styles.notice}>
          <Callout tone="danger" title="That didn't work">
            {pageError}
          </Callout>
        </div>
      ) : null}

      <section aria-labelledby="lender-documents" className={styles.section}>
        <div className={styles.sectionHead}>
          <div>
            <h2 className={styles.sectionTitle} id="lender-documents">
              Documents for clients
            </h2>
            <p className={styles.sectionLede}>
              Clients are given these in this order. Open one to see its history.
            </p>
          </div>
          {canEdit && documents.length > 0 ? (
            <Button icon={<Icon name="plus" />} onClick={() => openForm('add', null)}>
              Add document
            </Button>
          ) : null}
        </div>

        {documents.length === 0 ? (
          <EmptyState
            action={
              canEdit ? (
                <Button icon={<Icon name="plus" />} onClick={() => openForm('add', null)}>
                  Add document
                </Button>
              ) : null
            }
            icon="file-pdf"
            title="No documents yet"
          >
            <p>Upload the documents clients should be given for {lenderName}.</p>
          </EmptyState>
        ) : (
          <LenderDocumentList
            canEdit={canEdit}
            documents={documents}
            downloadingId={downloading}
            onDownload={(document) =>
              download(
                document.id,
                document.currentVersion?.fileId ?? null,
                titleOf(document),
                document.currentVersion?.file?.filename ?? null,
              )
            }
            onMove={onMove}
            onOpen={(document) => setOpenDocumentId(document.id)}
            reordering={reordering}
          />
        )}
      </section>

      {openDocument && !documentForm && !confirm ? (
        <DocumentHistory
          canEdit={canEdit}
          document={openDocument}
          downloadingVersionId={downloading}
          lenderName={lenderName}
          meId={me.data?.me.id ?? null}
          onArchive={() => ask({ kind: 'archive', document: openDocument })}
          onClose={() => setOpenDocumentId(null)}
          onDelete={() => ask({ kind: 'delete-document', document: openDocument })}
          onDownload={(version) =>
            download(
              version.id,
              version.fileId,
              version.title ?? titleOf(openDocument),
              version.file?.filename ?? null,
            )
          }
          onReplace={() => openForm('replace', openDocument)}
          onRestore={() => onRestore(openDocument)}
          onRetitle={() => openForm('retitle', openDocument)}
          onRevert={(version) => ask({ kind: 'revert', document: openDocument, version })}
        />
      ) : null}

      {editingLender ? (
        <LenderForm
          busy={busy}
          error={formError}
          initial={{
            key: lender.key ?? '',
            name: lender.name ?? '',
            country: lender.country ?? 'IE',
          }}
          mode="edit"
          onCancel={() => setEditingLender(false)}
          onSubmit={onSaveLender}
        />
      ) : null}

      {documentForm ? (
        <DocumentForm
          busy={busy}
          currentTitle={documentForm.document?.currentVersion?.title ?? ''}
          error={formError}
          lenderName={lenderName}
          mode={documentForm.mode}
          onCancel={() => setDocumentForm(null)}
          onSubmit={onSubmitDocument}
          progress={progress}
        />
      ) : null}

      {confirm ? (
        <ConfirmDialog
          busy={busy}
          error={formError}
          onCancel={() => setConfirm(null)}
          onConfirm={onConfirm}
          {...confirmCopy(confirm, lenderName)}
        />
      ) : null}
    </div>
  );
}

function confirmCopy(
  confirm: Confirm,
  lenderName: string,
): {
  title: string;
  children: string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  typeToConfirm?: string;
} {
  switch (confirm.kind) {
    case 'deactivate':
      return {
        title: `Deactivate ${lenderName}?`,
        children: `Clients will stop being shown ${lenderName} and its documents. Nothing is deleted, and you can reactivate it at any time.`,
        confirmLabel: 'Deactivate lender',
      };
    case 'delete-lender':
      return {
        title: `Delete ${lenderName}?`,
        children: `${lenderName} and all of its documents will be removed from LendWell. Its reference can never be used again. To hide it from clients for now, deactivate it instead.`,
        confirmLabel: 'Delete lender',
        typeToConfirm: lenderName,
      };
    case 'archive':
      return {
        title: `Archive “${titleOf(confirm.document)}”?`,
        children:
          'Clients will stop being given this document. Its history is kept, and you can restore it at any time.',
        confirmLabel: 'Archive document',
      };
    case 'delete-document':
      return {
        title: `Delete “${titleOf(confirm.document)}”?`,
        children:
          'This document and its history will be removed from this lender. To stop giving it to clients for now, archive it instead.',
        confirmLabel: 'Delete document',
      };
    case 'revert':
      return {
        title: `Restore version ${confirm.version.versionNo}?`,
        children: `Clients will be given “${confirm.version.title}” (version ${confirm.version.versionNo}) instead of the current version. No version is deleted, so you can switch back later.`,
        confirmLabel: `Restore version ${confirm.version.versionNo}`,
        tone: 'primary',
      };
  }
}
